import React, { useMemo } from 'react';
import { Box, CircularProgress, Fade, Typography } from '@mui/material';
import {
  useMediaStore,
  useMediaStoreReadonly,
} from '@brightmls/nextgen.lae.mediacenter';
import { useNavigate, useParams } from 'react-router-dom';
import { useTenantPath } from '../../../hooks/useTenantPath';

/**
 * Floating indicator shown on the main edit form when media items are
 * uploading or processing in the background. Reads directly from the
 * global MediaStore singleton.
 *
 * Positioned bottom-right. Clicking navigates to the media center.
 */
export const MediaProcessingIndicator: React.FC = () => {
  const navigate = useNavigate();
  const { listingKey } = useParams<{ listingKey: string }>();
  const { getPath } = useTenantPath();

  const {
    isUploading,
    currentUpload,
    overallUploadProgress,
    completedUploadsCount,
    totalBatchSize,
  } = useMediaStoreReadonly();

  const processingCount = useMediaStore(
    (state) => state.items.filter((item) => item._isProcessing).length,
  );

  const isVisible = isUploading || processingCount > 0;

  const statusText = useMemo(() => {
    if (isUploading && currentUpload) {
      return `Uploading ${completedUploadsCount + 1} of ${totalBatchSize}...`;
    }
    if (processingCount > 0) {
      return `${processingCount} item${processingCount !== 1 ? 's' : ''} processing...`;
    }
    return '';
  }, [
    isUploading,
    currentUpload,
    completedUploadsCount,
    totalBatchSize,
    processingCount,
  ]);

  const handleClick = () => {
    if (listingKey) {
      navigate(getPath(`/edit/${listingKey}/media/photos`));
    }
  };

  if (!isVisible) return null;

  return (
    <Fade in={isVisible}>
      <Box
        onClick={handleClick}
        sx={{
          position: 'fixed',
          bottom: 30,
          right: 30,
          zIndex: 1000,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 3,
          px: 2,
          py: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': {
            boxShadow: 6,
            bgcolor: 'action.hover',
          },
          transition: 'all 0.2s ease',
        }}
      >
        {isUploading ? (
          <CircularProgress
            variant="determinate"
            value={overallUploadProgress}
            size={24}
            thickness={4}
          />
        ) : (
          <CircularProgress size={24} thickness={4} />
        )}
        <Typography variant="body2" fontWeight={500}>
          {statusText}
        </Typography>
      </Box>
    </Fade>
  );
};
